import { provideAppInitializer } from '@angular/core';
import { DateTime, ToISOTimeOptions } from 'luxon';

declare module 'luxon' {
  interface ToISOTimeOptions {
    /**
     * Drop the trailing `Z` that {@link initializeLuxonIsoDefaults} appends,
     * yielding bare local wall time (`2026-08-17T14:30:00.000`).
     *
     * The `Z` exists for the OData literals, which need a zone designator on
     * comparisons the `DateInterceptor` rewrites. Pass `hideZ: true` wherever the
     * string is NOT going into a filter — anything that will be read back with
     * `fromISO`, or compared against `DateTimeService.toLocalObject` output,
     * which strips the suffix from its own values.
     */
    hideZ?: boolean;
  }
}

type ToISOFn = (this: DateTime, opts?: ToISOTimeOptions) => string | null;
type Patched = ToISOFn & { __isoDefaultsPatched?: true };

const originalToISO = DateTime.prototype.toISO as ToISOFn;

/**
 * The ISO shape the backend speaks: extended notation, milliseconds always
 * present, no `Z` and no `±HH:mm`.
 *
 * `suppressMilliseconds: false` and `format: 'extended'` are already Luxon's
 * own defaults — they are spelled out anyway so the contract is readable at
 * this one place instead of being inferred from the library, and so a future
 * Luxon default change cannot move the wire format underneath us.
 */
export const DEFAULT_ISO_OPTIONS: ToISOTimeOptions = {
  suppressMilliseconds: false,
  includeOffset: false,
  format: 'extended',
};

/**
 * Makes {@link DEFAULT_ISO_OPTIONS} the default of every `toISO()` in the app.
 *
 * Luxon has no global setting for ISO output — `Settings` only covers zone,
 * locale, `now` and `throwOnInvalid` — so the only way to reach the ~330 call
 * sites scattered across features (plus the ones inside `DateTimeService`) is
 * to default the options on the prototype itself.
 *
 * The backend speaks wall time: it receives and returns `2026-08-17T14:30:00.000`
 * with no `Z` and no `±HH:mm`, and the account timezone is applied by
 * {@link initializeLuxonZone}. Emitting the offset is what forced the
 * `replace(/Z$|[+-]\d{2}:\d{2}$/, '')` repeated across the codebase.
 *
 * ## What this changes
 *
 * Spreading `opts` last keeps the escape hatch open — a call site that really
 * wants the offset asks for it explicitly and still wins:
 *
 * ```ts
 * dateTime.toISO();                        // 2026-08-17T14:30:00.000
 * dateTime.toISO({ includeOffset: true }); // 2026-08-17T14:30:00.000-03:00
 * ```
 *
 * Two methods ride along, because in Luxon 3 both delegate to `toISO()`:
 * `toJSON()` (so a `DateTime` serialized inside a payload also drops the
 * offset — which is what the API expects) and `toString()` (logs, template
 * interpolation). `toISOTime()` and `toISODate()` have their own
 * implementations and are untouched.
 *
 * `DateTimeService.toUtcObject` still emits `Z`: its microsecond padding reads
 * the suffix off the matched string and falls back to `'Z'` when there is
 * none, so dropping the offset leaves its output byte-identical.
 *
 * ## When it lands
 *
 * At module evaluation (see the call right below the function), NOT at
 * `provideAppInitializer` time. App initializers run after every module in the
 * bundle has been evaluated, and stores build their initial state at module
 * scope — `ReceivementsPanelStore`'s `withState(createInitialState())` stamps a
 * `competenceMonth` through `toISO()` on load. Patching later would leave that
 * seed with an offset while `reset()` produced one without, so the two strings
 * would differ and every `rxMethod` bound to the signal would refire on reset.
 *
 * {@link LUXON_ISO_INITIALIZER} is kept anyway: it makes the dependency visible
 * in `appConfig` next to the other Luxon hooks, and covers a shell that reaches
 * this lib only through the provider. The guard makes that second call a no-op.
 */
export function initializeLuxonIsoDefaults(): void {
  if ((DateTime.prototype.toISO as Patched).__isoDefaultsPatched) {
    return; // Re-bootstrap (HMR, tests) — patching the patch would still work,
    // but the guard keeps the call stack flat.
  }

  const patchedToISO: Patched = function (
    this: DateTime,
    opts: ToISOTimeOptions = {}
  ) {
    const options = { ...(opts || {}) };
    const hideZ = options?.hideZ;
    delete options?.hideZ;
    const matchOpts = { ...DEFAULT_ISO_OPTIONS, ...options };
    const value = originalToISO.call(this, matchOpts)
    if (value === null) return null;
    return hideZ || matchOpts.includeOffset ? value : `${value}Z`;
  };
  patchedToISO.__isoDefaultsPatched = true;

  DateTime.prototype.toISO = patchedToISO as DateTime['toISO'];
}

// Side-effect on import: see "When it lands" above. Importing this module — via
// the `@application/data-access` barrel, which the feature stores already pull
// in — is what guarantees the patch precedes their module-scope `toISO()` calls.
initializeLuxonIsoDefaults();

/**
 * Provider for Luxon's ISO-output defaults, shared by every app shell.
 */
export const LUXON_ISO_INITIALIZER = provideAppInitializer(
  initializeLuxonIsoDefaults
);
